import { useRef, useState } from "react";
import { NiimbotSerialClient, ImageEncoder } from "@mmote/niimbluelib";

export const usePrinter = () => {
  const clientRef = useRef<any>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isPrinterConnected, setIsPrinterConnected] = useState(false);
  const [isSmallPrinter, setIsSmallPrinter] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);

  const repaint = (name: string, type: string, id: string) => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d", { willReadFrequently: true });
    if (!ctx || !canvas) return;

    ctx.fillStyle = "white";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const nameFont = isSmallPrinter ? "bold 24px Arial" : "bold 40px Arial";
    const idFont = isSmallPrinter ? "bold 32px Arial" : "bold 40px Arial";

    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;

    // Estimăm o distanță verticală între linii
    const lineSpacing = isSmallPrinter ? 32 : 44;

    ctx.font = nameFont;
    ctx.fillStyle = "black";
    ctx.fillText(name, centerX, centerY - lineSpacing / 2);

    ctx.font = idFont;
    ctx.fillText(`${type} [id: ${id}]`, centerX, centerY + lineSpacing / 2);
  };
  const connectPrinter = async () => {
    console.log("Deconectăm imprimanta.", clientRef.current);
    if (clientRef.current) {
      await clientRef.current.disconnect();
    }

    const client = new NiimbotSerialClient();

    client.on("connect", () => {
      console.log("Conectat la imprimantă.");
      setIsPrinterConnected(true);
    });

    client.on("disconnect", () => {
      console.log("Deconectat de la imprimantă.");
      setIsPrinterConnected(false);
    });

    clientRef.current = client;

    try {
      await client.connect();
    } catch (e) {
      alert("Eroare la conectare: " + e);
    }
  };

  const disconnectPrinter = async () => {
    console.log("Deconectăm imprimanta.");
    await clientRef.current?.disconnect();
    clientRef.current = null;
    setIsPrinterConnected(false);
  };

  const printLabel = async (name: string, type: string, id: string) => {
    if (isPrinting) {
      console.log("Procesul de printare este deja în desfășurare.");
      return;
    }

    if (!isPrinterConnected) {
      console.log("Imprimanta nu este conectată.");
      return;
    }

    console.log("Începem procesul de printare pentru:", name, type, id);
    setIsPrinting(true);

    try {
      repaint(name, type, id);

      const canvas = canvasRef.current;
      if (!canvas) throw new Error("Canvas-ul nu este disponibil.");

      const taskType = isSmallPrinter ? "D110" : "B1"; //clientRef.current?.getPrintTaskType?.() ?? "D110";

      const task = clientRef.current?.abstraction?.newPrintTask?.(taskType, {
        totalPages: 1,
        statusPollIntervalMs: 100,
        statusTimeoutMs: 8000,
      });

      if (!task) {
        console.log("Nu s-a putut crea task-ul de print.");
        return;
      }

      const encoded = ImageEncoder.encodeCanvas(
        canvas,
        isSmallPrinter ? "left" : "top"
      );
      console.log("Task creat, inițializăm...");

      const printTimeout = (ms: number) =>
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error("Timp de așteptare depășit")), ms)
        );

      await Promise.race([
        task.printInit(),
        printTimeout(5000), // Timeout de 5 secunde pentru printInit
      ]);
      console.log("Trimitem pagina...");
      await Promise.race([
        task.printPage(encoded, 1),
        printTimeout(5000), // Timeout de 5 secunde pentru printPage
      ]);

      console.log("Așteptăm finalizarea...");
      await Promise.race([
        task.waitForFinished(),
        new Promise((resolve) => setTimeout(resolve, 10000)), // fallback în caz că nu răspunde
      ]);

      console.log("Finalizăm printarea...");
      await clientRef.current?.abstraction?.printEnd();

      console.log("Printare completă.");
      setIsPrinting(false);
    } catch (e) {
      console.error("Eroare în timpul printării:", e);
      alert("Eroare în timpul printării: " + e.message);
    } finally {
      setIsPrinting(false);
    }
  };

  return {
    connectPrinter,
    disconnectPrinter,
    printLabel,
    setIsSmallPrinter,
    isSmallPrinter,
    canvasRef,
    isPrinterConnected,
    isPrinting,
  };
};
